import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getWallet, type Transaction } from "@/api/mockData";
import { t, formatRub, formatGNK, type Lang } from "@/lib/i18n";

interface WalletProps {
  lang: Lang;
}

export function Wallet({ lang }: WalletProps) {
  const wallet = getWallet();
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [address, setAddress] = useState("");
  const [txFilter, setTxFilter] = useState<"all" | "mining" | "withdrawal" | "bonus">("all");

  const handleWithdraw = (e: React.FormEvent) => {
    e.preventDefault();
    setWithdrawSuccess(true);
    setTimeout(() => {
      setWithdrawSuccess(false);
      setShowWithdraw(false);
      setWithdrawAmount("");
      setAddress("");
    }, 2000);
  };

  const filteredTx = txFilter === "all"
    ? wallet.transactions
    : wallet.transactions.filter((tx) => tx.type === txFilter);

  const filters: { key: typeof txFilter; label: string }[] = [
    { key: "all", label: lang === "ru" ? "Все" : "All" },
    { key: "mining", label: t("wallet.mining", lang) },
    { key: "bonus", label: t("wallet.bonus", lang) },
    { key: "withdrawal", label: t("wallet.withdrawal", lang) },
  ];

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="pt-3">
        <h1 className="font-display text-3xl font-black tracking-tight">{t("wallet.title", lang)}</h1>
      </div>

      {/* Balance Card */}
      <div className="glass-card-featured rounded-2xl overflow-hidden relative">
        <div className="h-[3px] bg-gradient-to-r from-primary via-primary/60 to-transparent" />

        {/* Decorative ambient glow */}
        <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none opacity-[0.04]" style={{
          background: "radial-gradient(circle at top right, rgba(220,38,38,0.3), transparent 70%)"
        }} />

        <div className="p-6 space-y-6 relative">
          <div>
            <p className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground/60 font-medium">{t("wallet.balance", lang)}</p>
            <p className="metric-hero text-[52px] text-gradient-cyan-amber mt-3">
              {formatGNK(wallet.balance)}
            </p>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-xs text-muted-foreground/70 font-mono-num">
                ≈ {formatRub(wallet.equivalentRub)}
              </span>
              <span className="text-[9px] px-2.5 py-1 rounded-full bg-chart-3/8 text-chart-3 font-semibold border border-chart-3/12">
                1 GNK = {formatRub(wallet.gnkPriceRub)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-secondary/25 rounded-xl p-4 border border-primary/10 accent-left-cyan">
              <p className="text-[8px] uppercase tracking-widest text-muted-foreground/60 font-medium">{t("wallet.pendingRewards", lang)}</p>
              <p className="font-mono-num text-lg font-bold text-primary mt-2">+{formatGNK(wallet.pendingRewards)}</p>
            </div>
            <div className="bg-secondary/25 rounded-xl p-4 border border-chart-3/10 accent-left-green">
              <p className="text-[8px] uppercase tracking-widest text-muted-foreground/60 font-medium">{t("wallet.todayEarnings", lang)}</p>
              <div className="flex items-center gap-1.5 mt-2">
                <span className="w-1.5 h-1.5 rounded-full bg-chart-3 mining-pulse" />
                <p className="font-mono-num text-lg font-bold text-chart-3">+{formatGNK(wallet.todayEarnings)}</p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowWithdraw(!showWithdraw)}
            className="w-full h-[56px] rounded-2xl cta-amber font-display flex items-center justify-center gap-2.5"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 17l9.2-9.2M17 17V7H7" />
            </svg>
            {t("wallet.withdraw", lang)}
          </button>
        </div>
      </div>

      {/* Withdraw Form */}
      {showWithdraw && (
        <Card className="glass-card border-0 animate-fade-in-up">
          <CardContent className="p-4">
            {withdrawSuccess ? (
              <div className="flex items-center gap-3 py-4 justify-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-chart-3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span className="text-chart-3 font-semibold">{t("deposit.success", lang)}</span>
              </div>
            ) : (
              <form onSubmit={handleWithdraw} className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                    {t("wallet.address", lang)}
                  </Label>
                  <Input
                    type="text"
                    placeholder="0x..."
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="h-11 font-mono-num text-sm bg-secondary/50 border-border/50 focus:border-primary"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                    {t("wallet.withdrawAmount", lang)}
                  </Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    className="h-11 font-mono-num text-sm bg-secondary/50 border-border/50 focus:border-primary"
                    max={wallet.balance}
                    min={1}
                    step="0.01"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setWithdrawAmount(String(wallet.balance))}
                    className="text-[10px] text-primary hover:underline"
                  >
                    MAX: {formatGNK(wallet.balance)} GNK
                  </button>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={() => setShowWithdraw(false)}
                    variant="outline"
                    className="flex-1 h-10 text-sm border-border/50"
                  >
                    {t("common.cancel", lang)}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!address || !withdrawAmount}
                    className="flex-1 h-10 text-sm bg-primary text-white hover:bg-primary/90 font-semibold disabled:opacity-40"
                  >
                    {t("wallet.confirm", lang)}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <section>
        <h2 className="section-label mb-4">
          {t("wallet.history", lang)}
        </h2>

        {/* Transaction Filters */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-none">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setTxFilter(f.key)}
              className={`px-3.5 py-1.5 rounded-xl text-[10px] font-semibold whitespace-nowrap transition-all duration-300 ${
                txFilter === f.key
                  ? "bg-primary/10 text-primary border border-primary/20"
                  : "bg-secondary/30 text-muted-foreground hover:text-foreground border border-transparent"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <Card className="glass-card border-0 rounded-2xl overflow-hidden">
          <CardContent className="p-0">
            {filteredTx.map((tx, i) => (
              <TransactionRow
                key={tx.id}
                tx={tx}
                lang={lang}
                last={i === filteredTx.length - 1}
              />
            ))}
            {filteredTx.length === 0 && (
              <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                {lang === "ru" ? "Нет транзакций" : "No transactions"}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function TransactionRow({ tx, lang, last }: { tx: Transaction; lang: Lang; last: boolean }) {
  const typeConfig: Record<string, { icon: string; color: string; label: string }> = {
    mining: { icon: "⛏", color: "text-chart-3", label: t("wallet.mining", lang) },
    withdrawal: { icon: "↗", color: "text-destructive", label: t("wallet.withdrawal", lang) },
    bonus: { icon: "🎁", color: "text-chart-2", label: t("wallet.bonus", lang) },
    deposit: { icon: "💳", color: "text-primary", label: lang === "ru" ? "Депозит" : "Deposit" },
  };

  const fallback: { icon: string; color: string; label: string } = { icon: "⛏", color: "text-chart-3", label: "—" };
  const cfg = typeConfig[tx.type] ?? fallback;
  const isPositive = tx.amount > 0;

  return (
    <div className={`flex items-center gap-3 px-4 py-3.5 ${!last ? "border-b border-white/[0.03]" : ""}`}>
      <div className="w-8 h-8 rounded-lg bg-secondary/50 flex items-center justify-center text-sm shrink-0">{cfg.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium truncate">{tx.description}</span>
          {tx.status === "pending" && (
            <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-chart-2/10 text-chart-2 border border-chart-2/15">pending</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-muted-foreground/70">{tx.date}</span>
          <span className="text-[10px] text-muted-foreground/40">·</span>
          <span className="text-[10px] text-muted-foreground/70">{cfg.label}</span>
        </div>
      </div>
      <span className={`font-mono-num text-[13px] font-bold ${isPositive ? cfg.color : "text-destructive"}`}>
        {isPositive ? "+" : ""}
        {tx.currency === "RUB" ? formatRub(tx.amount) : `${formatGNK(tx.amount)} GNK`}
      </span>
    </div>
  );
}
